// Canonical address representation used across admin + consumer surfaces.
// Mirrors `backend/common/address.py` - every API response wraps an address
// in `{display, value}`, and every input widget produces the same shape.
//
// Components that need to *render* an address read `.display`; components
// that need to *edit* an address read/write `.value`. Address widgets are
// responsible for keeping `display` in sync with `value` on every change
// via `buildAddress(value)`.

export interface AddressValue {
  address_line_1: string
  address_line_2: string
  locality: string
  region: string
  postal_code: string
  country_code: string
}

export interface Address {
  display: string
  value: AddressValue
}

export const EMPTY_ADDRESS_VALUE: AddressValue = {
  address_line_1: '',
  address_line_2: '',
  locality: '',
  region: '',
  postal_code: '',
  country_code: '',
}

const ALIASES: Record<keyof AddressValue, readonly string[]> = {
  address_line_1: ['address_line_1', 'addressLine1', 'line_1', 'line1'],
  address_line_2: ['address_line_2', 'addressLine2', 'line_2', 'line2'],
  locality: [
    'locality',
    'address_town_or_city',
    'townOrCity',
    'town_or_city',
    'city',
  ],
  region: ['region', 'address_county', 'county', 'state'],
  postal_code: [
    'postal_code',
    'postalCode',
    'address_postcode',
    'postcode',
    'zip_code',
    'zipCode',
    'zip',
  ],
  country_code: ['country_code', 'countryCode', 'address_country', 'country'],
}

function readAlias(source: Record<string, unknown>, key: keyof AddressValue): string {
  for (const alias of ALIASES[key]) {
    if (alias in source) {
      const v = source[alias]
      if (v == null) continue
      const text = String(v).trim()
      if (text) return text
    }
  }
  return ''
}

function buildDisplay(value: AddressValue): string {
  return [
    value.address_line_1,
    value.address_line_2,
    value.locality,
    value.region,
    value.postal_code,
    value.country_code,
  ]
    .filter((part) => !!part)
    .join(', ')
}

/**
 * Coerce any address-shaped input (canonical envelope, canonical value,
 * or any of the legacy variant shapes) into a canonical `AddressValue`.
 * Returns `EMPTY_ADDRESS_VALUE` for `null`/`undefined`/non-objects.
 */
export function normaliseAddressValue(source: unknown): AddressValue {
  if (source == null) return { ...EMPTY_ADDRESS_VALUE }
  if (typeof source === 'string') {
    // A pre-formatted display string is not editable structured data -
    // surface the user-provided text as line 1 so editing widgets get
    // *something* to show, but display will rebuild from line 1 only.
    const text = source.trim()
    return text
      ? { ...EMPTY_ADDRESS_VALUE, address_line_1: text }
      : { ...EMPTY_ADDRESS_VALUE }
  }
  if (typeof source !== 'object') return { ...EMPTY_ADDRESS_VALUE }
  const record = source as Record<string, unknown>
  // Unwrap a canonical envelope so callers can pass a server payload
  // (`{display, value}`) directly.
  const inner =
    'value' in record && typeof record.value === 'object' && record.value
      ? (record.value as Record<string, unknown>)
      : record
  return {
    address_line_1: readAlias(inner, 'address_line_1'),
    address_line_2: readAlias(inner, 'address_line_2'),
    locality: readAlias(inner, 'locality'),
    region: readAlias(inner, 'region'),
    postal_code: readAlias(inner, 'postal_code'),
    country_code: readAlias(inner, 'country_code'),
  }
}

/**
 * Build a canonical `Address` envelope from any address-shaped input,
 * computing the display string from the structured value. Returns
 * `null` for empty addresses so callers can suppress empty rows.
 */
export function buildAddress(source: unknown): Address | null {
  const value = normaliseAddressValue(source)
  const display = buildDisplay(value)
  if (!display) return null
  return { display, value }
}

/**
 * Read the human-readable string for a canonical `Address`, the
 * canonical `AddressValue`, or any legacy variant shape. Returns an
 * empty string for empty addresses.
 *
 * Components that already have an `Address` envelope from the API
 * should prefer reading `.display` directly; this helper exists for
 * the migration window where a value may still be in legacy form.
 */
export function addressDisplay(source: unknown): string {
  if (source == null) return ''
  if (typeof source === 'object' && 'display' in (source as object)) {
    const display = (source as { display: unknown }).display
    if (typeof display === 'string' && display.trim()) return display
  }
  return buildDisplay(normaliseAddressValue(source))
}

/**
 * Loose type guard: true when the value looks like *any* address shape
 * (canonical envelope, canonical value, or a legacy variant). Useful
 * when walking heterogeneous JSON looking for fields to render as an
 * address vs. raw text. Does not require all keys to be populated.
 */
export function isAddressLike(value: unknown): boolean {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false
  const record = value as Record<string, unknown>
  if ('display' in record && 'value' in record) return true
  for (const aliasList of Object.values(ALIASES)) {
    for (const alias of aliasList) {
      if (alias in record) return true
    }
  }
  return false
}

/** True when the value carries any non-empty structured field. */
export function isAddressNonEmpty(value: AddressValue): boolean {
  return (
    !!value.address_line_1 ||
    !!value.address_line_2 ||
    !!value.locality ||
    !!value.region ||
    !!value.postal_code ||
    !!value.country_code
  )
}

/** camelCase editable address shape consumed by the `AddressInput` widget. */
export interface EditableAddressValue {
  addressLine1: string
  addressLine2: string
  locality: string
  region: string
  postalCode: string
  countryCode: string
}

/**
 * Coerce any stored address shape - the canonical `{display, value}` envelope,
 * a bare canonical (snake_case) value, or an already-camelCase value - into the
 * camelCase shape `AddressInput` edits. Lets one ADDRESS form renderer accept
 * whatever the API or a sibling renderer persisted.
 */
export function toAddressInputValue(source: unknown): EditableAddressValue {
  const c = normaliseAddressValue(source)
  return {
    addressLine1: c.address_line_1,
    addressLine2: c.address_line_2,
    locality: c.locality,
    region: c.region,
    postalCode: c.postal_code,
    countryCode: c.country_code,
  }
}

/**
 * Structured address shape used by the shared `AddressCapture` component and
 * every admin call site (mirrors the legacy `ResolvedAddress` keys). Kept in
 * this non-component module so it can be imported without tripping the
 * component-file fast-refresh rule.
 */
export interface MailingAddress {
  line_1: string
  line_2: string
  town_or_city: string
  county: string
  postcode: string
  country: string
}

export const EMPTY_MAILING_ADDRESS: MailingAddress = {
  line_1: '',
  line_2: '',
  town_or_city: '',
  county: '',
  postcode: '',
  country: '',
}

/** Parts a required address needs when its field configures no `required_components`. */
export const DEFAULT_REQUIRED_ADDRESS_COMPONENTS: readonly (keyof MailingAddress)[] = [
  'postcode',
  'line_1',
  'town_or_city',
]

/** Coerce any stored/looked-up address shape into a `MailingAddress`. */
export function toMailingAddress(source: unknown): MailingAddress {
  const c = normaliseAddressValue(source)
  return {
    line_1: c.address_line_1,
    line_2: c.address_line_2,
    town_or_city: c.locality,
    county: c.region,
    postcode: c.postal_code,
    country: c.country_code,
  }
}

/** True when any structured component of a mailing address is filled. */
export function isMailingAddressNonEmpty(
  value: MailingAddress | null | undefined,
): boolean {
  return !!value && Object.values(value).some((v) => !!v?.trim())
}

/**
 * True when the value is a real, user-entered address worth showing as a
 * confirmed summary - i.e. it has a street line, town/city, or postcode. A
 * lone country (a default like `{ country: 'US' }`) does NOT count, so the
 * field still opens in the search/empty state rather than a misleading "US"
 * summary.
 */
export function isMeaningfulMailingAddress(
  value: MailingAddress | null | undefined,
): boolean {
  return (
    !!value &&
    (!!value.line_1?.trim() ||
      !!value.town_or_city?.trim() ||
      !!value.postcode?.trim())
  )
}

/**
 * Flat `Host` column shape. The API serialises a host's address as the
 * canonical `{display, value}` envelope (see `HostSummarySerializer`), but
 * the host *columns* - and therefore the registration wizard prefill and the
 * `change_address` entity action - are still keyed by these physical column
 * names. This is the bridge between the two.
 */
export interface HostAddressColumns {
  address_line_1: string
  address_line_2: string
  address_town_or_city: string
  address_county: string
  address_postcode: string
  address_country: string
}

/**
 * Coerce any stored address shape into the flat `Host` column names.
 *
 * Reach for this instead of reading `host.address_line_1` &c. directly: those
 * flat keys stopped being serialised when the canonical envelope landed, so a
 * direct read silently yields `undefined` and any `||` fallback behind it
 * (typically the stale, creation-time `host.name`) wins forever - the WPS-155
 * bug, where a registration header kept showing the pre-edit address.
 */
export function toHostAddressColumns(source: unknown): HostAddressColumns {
  const c = normaliseAddressValue(source)
  return {
    address_line_1: c.address_line_1,
    address_line_2: c.address_line_2,
    address_town_or_city: c.locality,
    address_county: c.region,
    address_postcode: c.postal_code,
    address_country: c.country_code,
  }
}
